import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/markdoc/tags/index.ts'
import * as nodes from '/vercel/path0/markdoc/nodes/index.ts'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer();

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"Launching the Covalent Blockchain Explorer: Live Charts for any network or transaction stat for Ethereum, Bitcoin, Bitcoin Gold, Bitcoin Cash, Ripple & more\"\ndate: 2018-01-01T04:25:15-08:00\ncategories: [\"product\"]\ndraft: true\ntype: legacy\ndescription: Explore live charts for any network, block, or transaction stat across 7 blockchains including Ethereum, Bitcoin, Bitcoin Gold, Bitcoin Cash, Ripple, and more with Covalent's universal blockchain explorer. Compare stats and track adoption of your favorite currencies with our easy-to-use interface.\n---\n\nToday, my team at Covalent is proud to announce the launch of our universal blockchain explorer. Our Explorer is the first to provide a live, visual interface over **7 blockchains**, including their underlying forks, currencies, and tokens. You can use it to compare any network-, block- or transaction- level stat across different blockchains over any period of time. Understanding the activity on a blockchain shouldn't involve downloading hundreds of gigabytes of data, complex code, or a PhD in statistics.\n\n\n\n\n\n\n## The Blockchain revolution is (almost) here\n\n2018 – I can finally talk about \"Bitcoin\" and \"Ethereum\" without getting blank stares. Just like the Internet revolutionized society 20 years ago, blockchain technologies have the massive potential to impact industries ranging from finance to retail to insurance and government. \n\nThus far, cryptocurrencies like Bitcoin and Ethereum have resonated with mainstream users. Billions of dollars have been raised through ICOs (Initial Coin Offerings) and the various cryptocurrencies that have had massive price rallies in 2017.\n\nAlthough cryptocurrencies have captured all of the mindshare, the bigger opportunity, in my opinion, is with **blockchains** with its decentralized architecture, immutable ledger and highly secure framework.\n\nBut today, there are several major technical challenges that make them impractical for mainstream adoption. \n\n**My team at Covalent is attempting to address these scaling challenges.**\n\nOur suite of services are positioned to help token companies and distributed apps handle the deluge of traffic associated with running a successful token launch or a production decentralized application.\n\n## Why we built a universal blockchain explorer\n\n![Image](https://www.covalenthq.com/static/images/blog/explorer-launch/schema-anim.gif) ![Image](https://www.covalenthq.com/static/images/blog/explorer-launch/photo-1466854076813-4aa9ac0fc347.png)![Image](https://www.covalenthq.com/static/images/blog/explorer-launch/photo-1467659226669-a1360d97be2d.png)\n \n\nWe wanted to address three pain-points with our first launch:\n\n1. Firstly, the tools available today are tied to a specific blockchain. Bitcoin has [Block Explorer](https://blockexplorer.com/), Ethereum has [Etherscan](https://etherscan.io/), Ripple has [XRP Charts](https://xrpcharts.ripple.com/#/), and so on. To our knowledge, there's nothing out there that allows you to compare the different blockchains side-by-side.\n\n2. Secondly, these tools target retail investors and so market data (prices) is their entire focus. Tools like [CoinMarketcap](https://coinmarketcap.com/) and [CoinGecko](https://www.coingecko.com/) are fantastic for market data and have integrations with the various cryptocurrency exchanges. We are different. Our \"direct connect\" technology integrates with dozens of blockchains to provide up-to-date, granular transaction-level data on these blockchains.\n\n3. Thirdly, the networks backing the blockchains live and breathe like an organism. They constantly exhibit various kinds of emergent behavior. One example of such behavior is the mechanism of soft- and hard- forks. The Bitcoin network alone has three or more blockchains like Bitcoin Classic, Bitcoin Gold, and Bitcoin Cash that have hard-forked off the same blockchain at some point in time. We worked hard to capture this dynamism.\n\n\n## How to Get Started\n\nIt's really easy to get started – if you haven't seen the link to the explorer yet, here it is:\n\n\n\nWith the explorer, you can:\n\n1. Personalize the widget by modifying the network, currency/token, date filters and aggregation windows to track the adoption of your favorite currencies.\n\n2. Embed the widget on an external website, especially useful if your team has run a token sale or operate a distributed application.\n\nMuch appreciation to those who share the explorer on social networks.\n\n\n\n## Customer Quote\n\nquotes from hypothetical customers that describe how they experienced the benefit.\n\n\n \n “\n Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante!\n \n \n Someone famous, Founder/CEO\n \n \n\n\n## Wrapping up\n\nI hope you find our tool useful and have as much fun playing with it as we had building it. If you have feature requests or want to report a bug, feel free to engage with our team via chat. \n\n\n";
const filepath = "/blog/universal-blockchain-explorer-launch.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
